import {
  SellerMemberBingoCellItemResponse,
  SellerMemberBingoDetailResponse,
} from "../../../api/pub-mission-member";
import ProgressStepper from "./progress-stepper";
import * as S from "../styles";

interface BingoDetailPanelProps {
  detail: SellerMemberBingoDetailResponse | null;
  loading: boolean;
  selectedCell: SellerMemberBingoCellItemResponse | null;
  progressInput: string;
  onSelectCell: (cellId: number, currentCount: number) => void;
  onProgressInputChange: (value: string) => void;
  onCompleteCell: () => void;
  onCancelCell: () => void;
  onSaveProgress: () => void;
}

const BingoDetailPanel = ({
  detail,
  loading,
  selectedCell,
  progressInput,
  onSelectCell,
  onProgressInputChange,
  onCompleteCell,
  onCancelCell,
  onSaveProgress,
}: BingoDetailPanelProps) => {
  if (loading) {
    return (
      <S.DetailPanel>
        <S.PanelHeader>빙고 상세</S.PanelHeader>
        <S.PanelBody>
          <S.EmptyMessage>불러오는 중...</S.EmptyMessage>
        </S.PanelBody>
      </S.DetailPanel>
    );
  }

  if (!detail) return null;

  const size = detail.column;
  const isCumulative = selectedCell?.type === "CUMULATIVE";
  const target = selectedCell?.targetCount ?? 0;
  const current = selectedCell?.currentCount ?? 0;
  const progressPercent =
    isCumulative && target > 0
      ? Math.min(100, Math.round((current / target) * 100))
      : 0;

  return (
    <S.DetailPanel>
      <S.PanelHeader>빙고 상세</S.PanelHeader>
      <S.PanelBody>
        <S.DetailTitle>{detail.name}</S.DetailTitle>
        <S.BadgeRow>
          <S.Badge>규격: {detail.sizeLabel}</S.Badge>
          <S.Badge $variant="purple">
            완성 줄: {detail.completedLines}줄
          </S.Badge>
        </S.BadgeRow>

        <S.BingoLayout>
          <S.BingoGridWrap>
            <S.BingoGrid $size={size}>
              {detail.cells.map((cell) => (
                <S.BingoCell
                  key={cell.id}
                  type="button"
                  $selected={selectedCell?.id === cell.id}
                  $complete={cell.isComplete}
                  onClick={() => onSelectCell(cell.id, cell.currentCount)}
                >
                  <div className="name">{cell.name}</div>
                  {cell.isComplete && <div>✓</div>}
                  {!cell.isComplete && cell.type === "CUMULATIVE" && (
                    <div className="progress">{cell.progressLabel}</div>
                  )}
                </S.BingoCell>
              ))}
            </S.BingoGrid>
            <S.SubText>칸을 클릭해 처리하세요.</S.SubText>
          </S.BingoGridWrap>

          {selectedCell && (
            <S.CellSidePanel>
              <S.SectionTitle>{selectedCell.name}</S.SectionTitle>
              <S.BadgeRow>
                <S.Badge>{selectedCell.progressStatusLabel}</S.Badge>
                <S.Badge>{selectedCell.typeLabel}</S.Badge>
              </S.BadgeRow>

              {isCumulative ? (
                <S.ProgressSection>
                  <S.ProgressText>
                    {current} / {target}
                  </S.ProgressText>
                  <S.ProgressBar>
                    <div
                      className="fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </S.ProgressBar>
                  <ProgressStepper
                    value={progressInput}
                    max={target}
                    onChange={onProgressInputChange}
                    onSave={onSaveProgress}
                  />
                  <S.SubText>
                    목표 수치 달성 시 완료 상태로 전환됩니다.
                  </S.SubText>
                </S.ProgressSection>
              ) : selectedCell.isComplete ? (
                <S.ActionButton
                  type="button"
                  $variant="danger"
                  onClick={onCancelCell}
                >
                  완료 취소
                </S.ActionButton>
              ) : (
                <S.ActionButton type="button" onClick={onCompleteCell}>
                  완료 처리
                </S.ActionButton>
              )}
            </S.CellSidePanel>
          )}
        </S.BingoLayout>

        <S.SectionTitle>달성 보상</S.SectionTitle>
        <S.RewardTable>
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>보상명</th>
              <th>유형</th>
              <th>수량</th>
              <th>달성 단계</th>
              <th>지급 상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {detail.rewards.map((reward) => (
              <tr key={reward.id}>
                <td>{reward.name}</td>
                <td>{reward.typeLabel}</td>
                <td>{reward.count ?? "-"}</td>
                <td>{reward.stageLabel}</td>
                <td>
                  <S.StatusBadge $status={reward.payoutStatusLabel}>
                    {reward.payoutStatusLabel}
                  </S.StatusBadge>
                </td>
                <td>—</td>
              </tr>
            ))}
          </tbody>
        </S.RewardTable>
      </S.PanelBody>
    </S.DetailPanel>
  );
};

export default BingoDetailPanel;
